
import { useState } from 'react';
import { ExpenseData, Invoice, DAILY_INVOICE_TYPES, BUSINESS_INVOICE_TYPES, ALLOWANCE_CITIES, ALLOWANCE_CITY_RATES } from '../types';
import { uploadInvoice } from '../../../services/storage';
import { recognizeInvoice } from '../../../services/ocr-cloud'; // 使用云函数版本
import { normalizeDateToYYYYMMDD } from '../../../utils/date-formatter';

interface InvoiceUploadStepProps {
  data: ExpenseData;
  onUpdate: (data: Partial<ExpenseData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

export default function InvoiceUploadStep({ data, onUpdate, onNext, onPrev }: InvoiceUploadStepProps) {
  const [dragActive, setDragActive] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showAllowanceForm, setShowAllowanceForm] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const currencySymbol = data.currencySymbol || '¥';
  const [manualInvoice, setManualInvoice] = useState<Partial<Invoice>>({
    amount: '',
    type: '',
    date: new Date().toISOString().split('T')[0],
    isManual: true
  });
  const [allowanceData, setAllowanceData] = useState({
    startDate: '',
    endDate: '',
    city: '',
    customAmount: ''
  });

  const invoiceTypes = data.expenseType === 'daily' ? DAILY_INVOICE_TYPES : BUSINESS_INVOICE_TYPES;

  // 检查是否所有发票都填写了类型
  const allInvoicesHaveType = data.invoices.length > 0 && data.invoices.every(inv => {
    if (inv.type === '自定义') {
      return inv.customType && inv.customType.trim() !== '';
    }
    return inv.type && inv.type.trim() !== '';
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFiles = async (files: File[]) => {
    const validFiles = files.filter(file =>
      file.type.includes('image') || file.type.includes('pdf')
    ).slice(0, 50 - data.invoices.length);

    // 使用局部变量跟踪当前发票列表，避免异步问题
    let currentInvoices = [...data.invoices];

    // Process files one by one with upload and OCR
    for (const file of validFiles) {
      try {
        // Create temporary invoice entry (while processing)
        const tempInvoice: Invoice = {
          id: Date.now() + Math.random().toString(),
          file,
          number: '识别中...',
          amount: 0,
          date: new Date().toISOString().split('T')[0],
          type: '', // Empty type, user needs to select
          isManual: false
        };

        // Add to list immediately so user sees progress
        currentInvoices = [...currentInvoices, tempInvoice];
        onUpdate({ invoices: currentInvoices });

        // Step 1: Upload file to Firebase Storage (optional - can be done later)
        // const uploadResult = await uploadInvoice(file, 'user-123');
        // if (!uploadResult.success) {
        //   throw new Error(uploadResult.error);
        // }

        // Step 2: Perform OCR recognition
        const ocrResult = await recognizeInvoice(file);

        // Normalize date format to YYYY-MM-DD
        const normalizedDate = normalizeDateToYYYYMMDD(ocrResult.date);

        // Check for duplicate invoice number (skip if invoice number is empty or "无票")
        let isDuplicateInvoice = false;
        let duplicateInvoiceId: string | null = null;
        if (ocrResult.invoiceNumber &&
            ocrResult.invoiceNumber !== '无票' &&
            ocrResult.invoiceNumber !== '识别中...') {
          const existingDuplicate = currentInvoices.find(inv =>
            inv.id !== tempInvoice.id &&
            inv.number === ocrResult.invoiceNumber
          );
          if (existingDuplicate) {
            isDuplicateInvoice = true;
            duplicateInvoiceId = existingDuplicate.id;
          }
        }

        // Step 3: Update invoice with OCR results (including duplicate flag)
        const recognizedInvoice: Invoice = {
          ...tempInvoice,
          number: ocrResult.invoiceNumber,
          amount: ocrResult.amount,
          date: normalizedDate, // Use normalized date
          isDuplicate: isDuplicateInvoice,
          type: '' // Empty type, user needs to select
        };

        // Update invoices list - 标记新上传的和已存在的重复发票
        currentInvoices = currentInvoices.map(inv => {
          if (inv.id === tempInvoice.id) {
            // 更新新上传的发票
            return recognizedInvoice;
          } else if (duplicateInvoiceId && inv.id === duplicateInvoiceId) {
            // 同时标记已存在的重复发票
            return { ...inv, isDuplicate: true };
          }
          return inv;
        });

        const totalAmount = currentInvoices.reduce((sum, inv) => sum + inv.amount, 0);

        onUpdate({
          invoices: currentInvoices,
          totalAmount
        });

      } catch (error: any) {
        console.error('Error processing invoice:', error);
        alert(`处理发票失败: ${file.name}\n${error.message}`);

        // 即使识别失败，也保留临时发票条目，让用户可以手动编辑
        // 不需要从列表中删除
      }
    }
  };

  const addManualInvoice = () => {
    const amount = Number(manualInvoice.amount);
    if (amount > 0 && manualInvoice.type) {
      const newInvoice: Invoice = {
        id: Date.now().toString(),
        file: null,
        number: '无票',
        amount: amount,
        date: manualInvoice.date || new Date().toISOString().split('T')[0],
        type: manualInvoice.type,
        isManual: true
      };

      const updatedInvoices = [...data.invoices, newInvoice];
      const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.amount, 0);

      onUpdate({
        invoices: updatedInvoices,
        totalAmount
      });

      setManualInvoice({
        amount: '',
        type: '',
        date: new Date().toISOString().split('T')[0],
        isManual: true
      });
      setShowManualForm(false);
    }
  };

  // 计算出差天数
  const calculateDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // 包含起始日和结束日
    return diffDays > 0 ? diffDays : 0;
  };

  // 计算出差补贴金额
  const calculateAllowanceAmount = (): number => {
    const { startDate, endDate, city, customAmount } = allowanceData;

    // 如果填写了自定义金额，直接使用
    if (customAmount && Number(customAmount) > 0) {
      return Number(customAmount);
    }

    // 否则按标准计算
    if (!startDate || !endDate || !city) return 0;

    const days = calculateDays(startDate, endDate);
    const rate = ALLOWANCE_CITY_RATES[city] || 80;
    return days * rate;
  };

  // 添加出差补贴
  const addAllowance = () => {
    const { startDate, endDate, city, customAmount } = allowanceData;

    if (!startDate || !endDate || !city) {
      alert('请填写完整的出差信息');
      return;
    }

    const amount = calculateAllowanceAmount();
    if (amount <= 0) {
      alert('补贴金额必须大于0');
      return;
    }

    const days = calculateDays(startDate, endDate);
    const newInvoice: Invoice = {
      id: Date.now().toString(),
      file: null,
      number: '出差补贴',
      amount: amount,
      date: startDate,
      type: '出差补贴',
      isManual: true,
      isAllowance: true,
      startDate: startDate,
      endDate: endDate,
      city: city
    };

    const updatedInvoices = [...data.invoices, newInvoice];
    const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.amount, 0);

    onUpdate({
      invoices: updatedInvoices,
      totalAmount
    });

    // 重置表单
    setAllowanceData({
      startDate: '',
      endDate: '',
      city: '',
      customAmount: ''
    });
    setShowAllowanceForm(false);
  };

  const removeInvoice = (id: string) => {
    const updatedInvoices = data.invoices.filter(inv => inv.id !== id);
    const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    
    onUpdate({ 
      invoices: updatedInvoices,
      totalAmount 
    });
  };

  const updateInvoice = (id: string, updates: Partial<Invoice>) => {
    const updatedInvoices = data.invoices.map(inv => 
      inv.id === id ? { ...inv, ...updates } : inv
    );
    const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    
    onUpdate({ 
      invoices: updatedInvoices,
      totalAmount 
    });
  };

  const showPreview = (invoice: Invoice) => {
    console.log('预览发票:', invoice);
    console.log('文件类型:', invoice.file?.type);
    console.log('文件名:', invoice.file?.name);
    setPreviewInvoice(invoice);
  };

  const closePreview = () => {
    setPreviewInvoice(null);
  };

  const getFilePreviewUrl = (file: File) => {
    return URL.createObjectURL(file);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">上传发票</h2>
        <p className="text-gray-600">支持批量上传，最多50张发票，系统将自动识别发票信息</p>
      </div>

      {/* 文件上传区域 */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="space-y-4">
          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
            <i className="ri-upload-cloud-2-line text-3xl text-blue-600"></i>
          </div>
          <div>
            <p className="text-xl font-semibold text-blue-600">拖拽文件到此处上传</p>
            <p className="text-sm text-gray-600 mt-2">支持批量拖拽多个文件</p>
          </div>
          <p className="text-sm text-gray-500">
            支持 JPG、PNG、PDF 格式，单次最多上传 {50 - data.invoices.length} 张
          </p>
        </div>
      </div>

      {/* 手动录入按钮 */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => setShowManualForm(true)}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          手动录入发票
        </button>
        {data.expenseType === 'business' && (
          <button
            onClick={() => setShowAllowanceForm(true)}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line mr-2"></i>
            出差补贴
          </button>
        )}
      </div>

      {/* 手动录入表单 */}
      {showManualForm && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">手动录入发票信息</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">金额</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={manualInvoice.amount}
                onChange={(e) => setManualInvoice(prev => ({ ...prev, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="请输入金额"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">发票类型</label>
              <select
                value={manualInvoice.type}
                onChange={(e) => setManualInvoice(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
              >
                <option value="">请选择类型</option>
                {invoiceTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">开票日期</label>
              <input
                type="date"
                value={manualInvoice.date}
                onChange={(e) => setManualInvoice(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setShowManualForm(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={addManualInvoice}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              添加
            </button>
          </div>
        </div>
      )}

      {/* 出差补贴表单 */}
      {showAllowanceForm && (
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-medium mb-4 text-blue-900">
            <i className="ri-calendar-check-line mr-2"></i>
            出差补贴计算
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">出发日期</label>
              <input
                type="date"
                value={allowanceData.startDate}
                onChange={(e) => setAllowanceData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">返回日期</label>
              <input
                type="date"
                value={allowanceData.endDate}
                onChange={(e) => setAllowanceData(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">出差城市</label>
              <select
                value={allowanceData.city}
                onChange={(e) => setAllowanceData(prev => ({ ...prev, city: e.target.value, customAmount: '' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
              >
                <option value="">请选择城市</option>
                {ALLOWANCE_CITIES.map(city => (
                  <option key={city} value={city}>
                    {city} ({ALLOWANCE_CITY_RATES[city]}元/天)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                自定义金额 <span className="text-xs text-gray-500">(可选，填写后将覆盖标准计算)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={allowanceData.customAmount}
                onChange={(e) => setAllowanceData(prev => ({ ...prev, customAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="如有特殊情况可直接填写"
              />
            </div>
          </div>

          {/* 实时显示计算结果 */}
          {allowanceData.startDate && allowanceData.endDate && allowanceData.city && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <p>出差天数: <span className="font-medium text-gray-900">{calculateDays(allowanceData.startDate, allowanceData.endDate)} 天</span></p>
                  {!allowanceData.customAmount && (
                    <p className="mt-1">标准: <span className="font-medium text-gray-900">{ALLOWANCE_CITY_RATES[allowanceData.city] || 80}元/天</span></p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">补贴金额</p>
                  <p className="text-2xl font-bold text-blue-600">{currencySymbol}{calculateAllowanceAmount().toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => {
                setShowAllowanceForm(false);
                setAllowanceData({ startDate: '', endDate: '', city: '', customAmount: '' });
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={addAllowance}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1"></i>
              添加补贴
            </button>
          </div>
        </div>
      )}

      {/* 发票列表 */}
      {data.invoices.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">已上传发票 ({data.invoices.length})</h3>
          <div className="space-y-3">
            {data.invoices.map((invoice) => (
              <div key={invoice.id} className="bg-gray-50 rounded-lg p-4">
                {/* Duplicate warning banner */}
                {invoice.isDuplicate && (
                  <div className="mb-3 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded-md flex items-center space-x-2">
                    <i className="ri-alert-line text-yellow-600"></i>
                    <span className="text-sm text-yellow-800">
                      <strong>⚠️ 警告：</strong>检测到可能重复的发票号（{invoice.number}）。请确认是否需要保留此发票。
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center relative ${
                      invoice.isAllowance ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <i className={`${
                        invoice.isAllowance
                          ? 'text-green-600 ri-calendar-check-line'
                          : invoice.isManual
                            ? 'text-blue-600 ri-edit-line'
                            : 'text-blue-600 ri-file-text-line'
                      }`}></i>
                      {invoice.file && (
                        <button
                          onClick={() => showPreview(invoice)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-green-600 cursor-pointer transition-colors"
                          title="预览发票"
                        >
                          <i className="ri-eye-line text-xs"></i>
                        </button>
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          {invoice.isAllowance ? '补贴信息' : '发票号'}
                        </label>
                        {invoice.isAllowance ? (
                          <div className="text-sm">
                            <div className="font-medium text-green-700">{invoice.city}</div>
                            <div className="text-xs text-gray-600">
                              {invoice.startDate} ~ {invoice.endDate}
                            </div>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={invoice.number}
                            onChange={(e) => updateInvoice(invoice.id, { number: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="发票号"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">金额</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={invoice.amount || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === '' ? 0 : Number(value);
                            updateInvoice(invoice.id, { amount: numValue });
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          类型 {!invoice.type && <span className="text-red-500">*必填</span>}
                        </label>
                        {invoice.isAllowance ? (
                          <div className="w-full px-2 py-1 text-sm bg-gray-100 border border-gray-300 rounded text-gray-700">
                            出差补贴
                          </div>
                        ) : invoice.type === '自定义' ? (
                          <input
                            type="text"
                            value={invoice.customType || ''}
                            onChange={(e) => updateInvoice(invoice.id, { customType: e.target.value })}
                            className={`w-full px-2 py-1 text-sm border rounded ${
                              !invoice.customType?.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="自定义（待填写）"
                            onBlur={(e) => {
                              // 如果输入框为空，则清空自定义选择
                              if (!e.target.value.trim()) {
                                updateInvoice(invoice.id, { type: '', customType: undefined });
                              }
                            }}
                          />
                        ) : (
                          <select
                            value={invoice.type}
                            onChange={(e) => {
                              const newType = e.target.value;
                              updateInvoice(invoice.id, {
                                type: newType,
                                customType: newType === '自定义' ? '' : undefined
                              });
                            }}
                            className={`w-full px-2 py-1 text-sm border rounded pr-8 ${
                              !invoice.type ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                          >
                            <option value="">请选择费用类型</option>
                            {invoiceTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">开票日期</label>
                        <input
                          type="date"
                          value={invoice.date}
                          onChange={(e) => updateInvoice(invoice.id, { date: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeInvoice(invoice.id)}
                    className="ml-4 text-red-500 hover:text-red-700 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* 总金额 */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-700">总金额：</span>
              <span className="text-2xl font-bold text-blue-600">{currencySymbol}{data.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* 发票预览弹窗 */}
      {previewInvoice && previewInvoice.file && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">发票预览</h3>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-180px)] bg-gray-100">
              {previewInvoice.file.type.includes('image') ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <img
                    src={getFilePreviewUrl(previewInvoice.file)}
                    alt="发票预览"
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg bg-white"
                  />
                </div>
              ) : previewInvoice.file.type.includes('pdf') ? (
                <div className="w-full h-[600px]">
                  <iframe
                    src={getFilePreviewUrl(previewInvoice.file)}
                    className="w-full h-full rounded-lg border-0"
                    title="PDF 预览"
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-file-line text-6xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600">无法预览此文件类型</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">发票号：</span>
                  <span className="font-medium">{previewInvoice.number || '未填写'}</span>
                </div>
                <div>
                  <span className="text-gray-500">金额：</span>
                  <span className="font-medium">{currencySymbol}{previewInvoice.amount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-500">类型：</span>
                  <span className="font-medium">
                    {previewInvoice.type === '自定义'
                      ? (previewInvoice.customType || '自定义（待填写）')
                      : (previewInvoice.type || '未选择')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">日期：</span>
                  <span className="font-medium">{previewInvoice.date}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 验证提示 */}
      {data.invoices.length > 0 && !allInvoicesHaveType && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-start space-x-3">
          <i className="ri-error-warning-line text-yellow-600 text-xl mt-0.5"></i>
          <div className="flex-1">
            <p className="text-yellow-800 font-medium">请完成所有发票的类型填写</p>
            <p className="text-yellow-700 text-sm mt-1">
              所有发票都必须选择类型，如果选择"自定义"，需要填写具体的类型名称
            </p>
          </div>
        </div>
      )}

      {/* 导航按钮 */}
      <div className="flex justify-between pt-6">
        <button
          onClick={onPrev}
          className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          上一步
        </button>
        <button
          onClick={onNext}
          disabled={!allInvoicesHaveType}
          className={`px-8 py-3 rounded-lg transition-colors font-medium whitespace-nowrap ${
            allInvoicesHaveType
              ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={!allInvoicesHaveType ? '请先完成所有发票的类型填写' : ''}
        >
          下一步 <i className="ri-arrow-right-line ml-2"></i>
        </button>
      </div>
    </div>
  );
}
